import type { DimensionsMm, IsoDateTime, PhysicalSpec } from './common';
import { isValidGtin } from './gs1';
import type { BarcodeId, PackagingId, ProductId, TenantId } from './ids';

export const PRODUCT_STATUSES = ['active', 'inactive', 'archived'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const BARCODE_SYMBOLOGIES = ['ean8', 'upc_a', 'ean13', 'itf14', 'gs1_128', 'code128', 'qr', 'other'] as const;
export type BarcodeSymbology = (typeof BARCODE_SYMBOLOGIES)[number];

/** each = sellable unit; inner / case (koli) / pallet = how it is received and stored. */
export const PACKAGING_LEVELS = ['each', 'inner', 'case', 'pallet'] as const;
export type PackagingLevel = (typeof PACKAGING_LEVELS)[number];

export interface Product {
  readonly id: ProductId;
  readonly tenantId: TenantId;
  /** Seller's stock code, unique per tenant (two sellers may use the same SKU). */
  readonly sku: string;
  readonly name: string;
  readonly description: string | null;
  readonly brand: string | null;
  readonly category: string | null;
  /** Shown on the picker and packing screens. */
  readonly imageUrl: string | null;
  /** Each-level physical spec; null until the seller enters it or receiving measures it. */
  readonly lengthMm: number | null;
  readonly widthMm: number | null;
  readonly heightMm: number | null;
  readonly grossWeightG: number | null;
  /** Generated columns in PostgreSQL. */
  readonly volumeCm3: number | null;
  readonly desi: number | null;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
  /** Outbound rule: allocate only stock with at least this many days of shelf life left. */
  readonly minShelfLifeDays: number | null;
  readonly isFragile: boolean;
  /** false: no barcode label may be stuck on the goods (shelf card, confirmed by sight at packing) */
  readonly unitLabels: boolean;
  /** true: leaves in its own box, the label stuck on it (each unit a box); its orders skip the packing table */
  readonly shipsAsIs: boolean;
  /** with shipsAsIs: the label is printed when the pick route is made and taken along to the bin */
  readonly labelBeforePick: boolean;
  readonly status: ProductStatus;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

/** SKU ↔ barcode (EAN/GTIN or the seller's own code). Unique per tenant, not globally. */
export interface ProductBarcode {
  readonly id: BarcodeId;
  readonly tenantId: TenantId;
  readonly productId: ProductId;
  /** Normalised: no whitespace, upper-case ASCII. GTIN symbologies carry a valid GS1 check digit. */
  readonly barcode: string;
  readonly symbology: BarcodeSymbology;
  readonly packagingLevel: PackagingLevel;
  /** Units counted by one scan: 1 for each, e.g. 24 for an ITF-14 case barcode. */
  readonly unitsPerScan: number;
  readonly isPrimary: boolean;
  readonly createdAt: IsoDateTime;
}

/** A packaging level above the unit (inner pack, case, pallet); one row of the desi matrix. */
export interface ProductPackaging extends PhysicalSpec {
  readonly id: PackagingId;
  readonly tenantId: TenantId;
  readonly productId: ProductId;
  readonly level: Exclude<PackagingLevel, 'each'>;
  readonly unitsPerPack: number;
  readonly desi: number;
}

export interface DesiMatrixRow extends PhysicalSpec {
  readonly level: PackagingLevel;
  readonly units: number;
  readonly desi: number;
  /** max(desi, kg): what carriers bill, before rounding up to their own price steps. */
  readonly chargeableDesi: number;
}

/** Turkish carriers' volumetric divisor: desi = en × boy × yükseklik (cm) / 3000. */
export const DESI_DIVISOR_CM3 = 3000;

/** Same value and rounding as the generated column products.desi. */
export function desiOf(d: DimensionsMm): number {
  // mm³ / 30 000 = desi × 100. Dividing exact integers keeps the .5 cases equal to SQL round().
  return Math.round((d.lengthMm * d.widthMm * d.heightMm) / ((DESI_DIVISOR_CM3 * 1000) / 100)) / 100;
}

export function chargeableDesi(spec: PhysicalSpec): number {
  return Math.max(desiOf(spec), spec.grossWeightG / 1000);
}

/**
 * Parcel desi from its contents when no carton was recorded: unit desi × quantity plus a
 * packing allowance; unmeasured products count as defaultUnitDesi.
 */
export function estimateParcelDesi(
  items: readonly { readonly unitDesi: number | null; readonly qty: number }[],
  defaultUnitDesi = 1,
  packingFactor = 1.2,
): number {
  const total = items.reduce((sum, i) => sum + (i.unitDesi ?? defaultUnitDesi) * i.qty, 0);
  return Math.round(total * packingFactor * 100) / 100;
}

/**
 * Desi matrisi: the unit row (when the product is measured) plus one row per packaging level,
 * smallest first. Used for cartonisation and carrier pricing.
 */
export function buildDesiMatrix(
  product: Pick<Product, 'lengthMm' | 'widthMm' | 'heightMm' | 'grossWeightG'>,
  packagings: readonly ProductPackaging[],
): DesiMatrixRow[] {
  const rows: DesiMatrixRow[] = [];
  const { lengthMm, widthMm, heightMm, grossWeightG } = product;
  if (lengthMm !== null && widthMm !== null && heightMm !== null && grossWeightG !== null) {
    rows.push(matrixRow('each', 1, { lengthMm, widthMm, heightMm, grossWeightG }));
  }
  for (const p of [...packagings].sort((a, b) => a.unitsPerPack - b.unitsPerPack)) {
    rows.push(matrixRow(p.level, p.unitsPerPack, p));
  }
  return rows;
}

function matrixRow(level: PackagingLevel, units: number, spec: PhysicalSpec): DesiMatrixRow {
  const { lengthMm, widthMm, heightMm, grossWeightG } = spec;
  return {
    level,
    units,
    lengthMm,
    widthMm,
    heightMm,
    grossWeightG,
    desi: desiOf(spec),
    chargeableDesi: chargeableDesi(spec),
  };
}

export const PACKAGING_LEVEL_NAMES: Readonly<Record<PackagingLevel, string>> = { each: 'Adet', inner: 'İç koli', case: 'Koli', pallet: 'Palet' };

/**
 * The symbology of a typed or scanned code: GTIN lengths with a valid check digit are
 * EAN-8 / UPC-A / EAN-13 / ITF-14, other printable codes are the seller's own (Code 128).
 * A GTIN-length number with a wrong check digit is a typing error: null.
 */
export function detectSymbology(code: string): BarcodeSymbology | null {
  if (/^\d+$/.test(code) && [8, 12, 13, 14].includes(code.length)) {
    if (!isValidGtin(code)) return null;
    return code.length === 8 ? 'ean8' : code.length === 12 ? 'upc_a' : code.length === 13 ? 'ean13' : 'itf14';
  }
  return /^[!-~]{3,64}$/.test(code) ? 'code128' : null;
}

/**
 * Units in one sale of a marketplace item named as a set ("2'li set", "6'lı paket", "3 adet");
 * null for a single unit. Such an item is mapped by a person, with its count.
 */
export function packCount(title: string): number | null {
  const found = /(\d+)\s*['’]?\s*l[iıuüİ](?![a-zçğıöşüİ])/i.exec(title) ?? /(?<!\d)(\d+)\s*adet(?![a-zçğıöşüİ])/i.exec(title);
  const n = found ? Number(found[1]) : NaN;
  return Number.isInteger(n) && n >= 2 && n <= 1000 ? n : null;
}

/** A barcode of the sellable unit, or of a packaging level (counting its units per scan). */
export interface BarcodeInput {
  readonly barcode: string;
  readonly isPrimary?: boolean;
}

export interface PackagingInput {
  readonly level: Exclude<PackagingLevel, 'each'>;
  readonly unitsPerPack: number;
  readonly lengthMm: number;
  readonly widthMm: number;
  readonly heightMm: number;
  readonly grossWeightG: number;
  /** printed on the packaging (ITF-14 or EAN), counts unitsPerPack per scan */
  readonly barcode?: string | null;
}

/** A product as the seller (or the warehouse for the seller) defines it. */
export interface ProductInput {
  readonly sku: string;
  readonly name: string;
  readonly description?: string | null;
  readonly brand?: string | null;
  readonly category?: string | null;
  readonly imageUrl?: string | null;
  readonly lengthMm?: number | null;
  readonly widthMm?: number | null;
  readonly heightMm?: number | null;
  readonly grossWeightG?: number | null;
  readonly trackLot?: boolean;
  readonly trackExpiry?: boolean;
  readonly minShelfLifeDays?: number | null;
  readonly isFragile?: boolean;
  readonly unitLabels?: boolean;
  readonly shipsAsIs?: boolean;
  readonly labelBeforePick?: boolean;
  readonly status?: ProductStatus;
  /** unit barcodes; the first primary one (or the first) is the product's main barcode */
  readonly barcodes?: readonly BarcodeInput[];
  readonly packagings?: readonly PackagingInput[];
}

/** One row of the catalog list. */
export interface ProductListItem {
  readonly id: ProductId;
  readonly tenantId: TenantId;
  readonly tenantCode: string;
  readonly sku: string;
  readonly name: string;
  readonly brand: string | null;
  readonly imageUrl: string | null;
  readonly primaryBarcode: string | null;
  readonly desi: number | null;
  readonly status: ProductStatus;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
  /** false: no label is stuck on the goods (a card for the shelf instead) */
  readonly unitLabels: boolean;
  readonly packagings: readonly { readonly level: Exclude<PackagingLevel, 'each'>; readonly unitsPerPack: number }[];
  readonly physical: number;
  readonly available: number;
  /** marketplace listings mapped to the product */
  readonly listings: number;
}

/** A product with everything the form edits. */
export interface ProductDetail extends Product {
  readonly tenantCode: string;
  readonly barcodes: readonly { readonly barcode: string; readonly symbology: BarcodeSymbology; readonly packagingLevel: PackagingLevel; readonly unitsPerScan: number; readonly isPrimary: boolean }[];
  readonly packagings: readonly (PackagingInput & { readonly desi: number })[];
  readonly physical: number;
  readonly available: number;
}

/** A problem in one cell of an imported spreadsheet (row 1 is the header). */
export interface ImportProblem {
  readonly row: number;
  readonly column: string | null;
  readonly message: string;
}

/** What an import would do (dry run) or did. */
export interface CatalogImportResult {
  readonly dryRun: boolean;
  readonly rows: number;
  readonly created: number;
  readonly updated: number;
  readonly unchanged: number;
  readonly errors: readonly ImportProblem[];
  /** recognised columns, in sheet order */
  readonly columns: readonly string[];
  /** columns the template does not know (ignored) */
  readonly ignoredColumns: readonly string[];
  readonly preview: readonly { readonly row: number; readonly sku: string; readonly name: string; readonly barcode: string | null; readonly action: 'create' | 'update' | 'unchanged' | 'error' }[];
}
